<template>
    <div
        v-if="active"
        class="fixed z-9999 inset-0 flex items-center justify-center p-6"
    >
        <div
            class="absolute inset-0 z-10 bg-overlay-black-50"
            @click="dismissPopup"
        />

        <div class="relative z-20 max-h-[570px] w-full max-w-[470px] flex flex-col">
            <DButton
                class="z-10 absolute! right-0 -top-12"
                data-mode="base-invert"
                theme="transparent"
                icon="x"
                icon-set="ph"
                @click="dismissPopup"
            />

            <div
                :data-theme="data.colorTheme"
                :data-mode="mode"
                class="flex flex-col surface-primary rounded-modal-md overflow-hidden shadow-xl"
            >
                <div class="flex-1 overflow-hidden relative">
                    <img v-if="!isEmpty(popup.image)" :src="popup.image" class="w-full h-full object-cover">
                </div>
                <div class="flex-1 shrink-0 px-6 py-4 space-y-4">
                    <div class="space-y-1">
                        <h2 class="text-lg leading-lg font-brand-heading text-neutral-base font-heading">
                            {{ popup.heading }}
                        </h2>


                        <div v-if="popupEntry" class="space-y-2 pt-2">
                            <small v-if="popupEntry.startDate" class="text-neutral-base text-sm leading-sm font-ux flex items-start lg:items-center space-x-2">
                                <DIcon icon="calendar-blank" icon-set="ph" size="w-5 h-5" class="shape-brand-accent" />
                                <DateFormat
                                    :date="popupEntry.startDate"
                                    :end="popupEntry.endDate"
                                />
                            </small>

                            <small v-if="popupEntry.startDate && !popupEntry.endDate" class="text-neutral-base text-sm leading-sm font-ux flex items-start lg:items-center space-x-2">
                                <DIcon icon="clock" icon-set="ph" size="w-5 h-5" class="shape-brand-accent" />
                                <DateFormat :date="popupEntry.startDate" :end="popupEntry.endDate" format="LT" />
                            </small>

                            <small v-if="popupEntry.location" class="text-neutral-base text-sm leading-sm font-ux items-start lg:items-center space-x-2 hidden md:flex">
                                <DIcon icon="map-pin" icon-set="ph" size="w-5 h-5" class="shape-brand-accent" />
                                <span>{{popupEntry.location}}</span>
                            </small>
                        </div>

                        <h3
                            v-if="popup.subheading"
                            class="text-sm text-neutral-base font-normal"
                        >
                            {{ popup.subheading }}
                        </h3>
                    </div>

                    <p class="text-sm text-neutral-subtle whitespace-pre-wrap font-ux line-clamp-5">
                        {{ popup.bodyText }}
                    </p>

                </div>

                <div class="px-6 py-3 mt-auto flex flex-col-reverse md:flex-row gap-3">
                    <DButton
                        theme="base"
                        wide
                        @click="dismissPopup"
                    >
                        Dismiss
                    </DButton>
                    <NuxtLink
                        v-if="popup.button"
                        :to="popup.button.url"
                        :target="popup.button.target"
                        class="flex-1 w-full"
                    >
                        <DButton
                            wide
                            @click="markAsRead"
                        >
                            {{ popup.button.text }}
                        </DButton>
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { useScrollLock } from '@vueuse/core'
    import { DButton, DIcon } from '@digistorm/spark'

    const { cmsQuery } = useGraphQL()
    const scrollLocked = useScrollLock(window)
    const { entry } = storeToRefs(useEntryStore())

    const active = ref(false)

    const data = ref(null)

    const popupData = computed(() => {
        return first(entry.value?.popup)
    })

    const cookieName = `popup:${popupData.value?.canonicalUid}`
    const popupRead = useCookie(cookieName)

    const popupContent = computed(() => {
        return get(data.value, 'ds_PopupsEntries.0', {})
    })

    // this is only for the articleOrEvent content type
    const popupEntry = computed(() => {
        return get(popupContent.value, 'entry.0', {})
    })

    const popup = computed(() => {
        let content = {}

        if (popupContent.value.contentType === 'articleOrEvent') {
            if (isEmpty(popupEntry.value)) {
                return null
            }

            content = {
                'image': popupEntry.value.image[0]?.url,
                'heading': popupEntry.value.title,
                'bodyText': popupEntry.value.eventExcerpt ?? popupEntry.value.articleExcerpt,
                'button': {
                    'text': 'Read more',
                    'url': popupEntry.value.url,
                    'target': ''
                }
            }

        } else  {
            content = {
                'image': get(popupContent.value, 'image.0.url'),
                'heading': popupContent.value.heading,
                'subheading': popupContent.value.subheading,
                'bodyText': popupContent.value.bodyText,
            }

            if (has(popupContent.value, 'button')) {
                content.button = {
                    'url': get(popupContent.value.button, '0.url'),
                    'text': get(popupContent.value.button, '0.text', 'Read more'),
                    'target': get(popupContent.value.button, '0.target', ''),
                }
            }
        }

        return content
    })



    const mode = computed(() => {
        switch (data.value?.colorPalette) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return data.value?.colorPalette
        }
    })

    const markAsRead = () => {
        popupRead.value = true
    }

    const dismissPopup = () => {
        markAsRead()
        scrollLocked.value = false
        active.value = false
    }

    onMounted(() => {
        nextTick(() => {
            // if there is no popup data, return
            if (!popupData.value) {
                return
            }

            cmsQuery({
                uri: `entry-data/ds_PopupsEntries/${popupData.value?.canonicalUid}`,
                key: `popup-${popupData.value?.canonicalUid}`,
                server: false,
            }).then((response) => {
                data.value = response.data

                if (popup.value && !popupRead.value) {
                    active.value = true
                    scrollLocked.value = true
                }
            })
        })
    })

</script>

